import {
  type CreateItineraryComponentRequest,
  type ItineraryActivityTransferMode,
  type TransferCategory,
  type TransferDetail,
  type TransferMode,
  TransferCategorySchema,
  TransferModeSchema,
  TransferTypeSchema,
  isPublicTransferMode,
} from '@utazzo/shared';
import { UzButton, UzInput, UzSelect } from '@utazzo/ui';
import {
  Bus,
  Car,
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  MapPin,
  Plus,
  RotateCcw,
  Ship,
  Ticket,
  TrainFront,
  Users,
  X,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { PriceDelta } from './price-visibility';

import { useActivityDetail, useActivitiesList } from '@/features/activities/use-activities';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useFxRate } from '@/features/currencies/use-currencies';
import { useTransferDetail, useTransfersList } from '@/features/transfers/use-transfers';

/**
 * Day-item picker (Phase 6). A big modal - not a drawer - with a left filter
 * rail and a full, scrollable result list for choosing activities or internal
 * transfers from our DB, scoped to the day's city.
 *
 * Activities open a variant step: pick a ticket tier, then a transfer add-on
 * (SIC / Private) - hidden when the tier already includes a transfer or no
 * priced option exists. Selecting adds it to the day; the modal stays open so
 * the agent can add several in a row.
 */

type Tab = 'ACTIVITY' | 'TRANSFER';

/**
 * 2026-07-06. Sort keys the picker offers. All client-side sort
 * because the list endpoints don't (yet) accept `sortBy: 'price'` /
 * `'rating'` - the shared PaginationQuerySchema `sortBy` is
 * schema-agnostic and would route to a name column anyway.
 */
type SortKey = 'name' | 'price-asc' | 'price-desc' | 'rating-desc' | 'recent';
const SORT_OPTIONS: ReadonlyArray<{ value: SortKey; label: string }> = [
  { value: 'name', label: 'Name (A - Z)' },
  { value: 'price-asc', label: 'Price (low first)' },
  { value: 'price-desc', label: 'Price (high first)' },
  { value: 'rating-desc', label: 'Rating (top first)' },
  { value: 'recent', label: 'Recently added' },
];

/**
 * 2026-07-06. Generic client-side sort. Works for both Activity and
 * Transfer rows via getters supplied by the caller so the picker
 * doesn't need two duplicate sort functions. Rows with a null / NaN
 * price sort last on price-asc and first on price-desc (they're
 * "unpriced", not "cheap").
 */
function applySort<T extends { name: string }>(
  rows: readonly T[],
  key: SortKey,
  priceOf: (r: T) => number | null | undefined,
  ratingOf: (r: T) => number,
  createdAtOf: (r: T) => string,
): T[] {
  const list = [...rows];
  const nameCmp = (a: T, b: T): number => a.name.localeCompare(b.name);
  switch (key) {
    case 'name':
      return list.sort(nameCmp);
    case 'price-asc':
      return list.sort((a, b) => {
        const pa = priceOf(a);
        const pb = priceOf(b);
        const na = pa === null || pa === undefined || !Number.isFinite(pa) ? Infinity : pa;
        const nb = pb === null || pb === undefined || !Number.isFinite(pb) ? Infinity : pb;
        return na - nb || nameCmp(a, b);
      });
    case 'price-desc':
      return list.sort((a, b) => {
        const pa = priceOf(a);
        const pb = priceOf(b);
        const na = pa === null || pa === undefined || !Number.isFinite(pa) ? -Infinity : pa;
        const nb = pb === null || pb === undefined || !Number.isFinite(pb) ? -Infinity : pb;
        return nb - na || nameCmp(a, b);
      });
    case 'rating-desc':
      return list.sort((a, b) => ratingOf(b) - ratingOf(a) || nameCmp(a, b));
    case 'recent':
      return list.sort((a, b) => (createdAtOf(b) < createdAtOf(a) ? -1 : 1));
    default:
      return list;
  }
}

const titleCase = (s: string): string =>
  s
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const money = (n: number, currency: string): string => {
  if (!Number.isFinite(n)) return '-';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${currency} ${Math.round(n).toLocaleString('en-IN')}`;
  }
};

/**
 * 2026-07-07. Render an amount in INR after converting it from `srcCurrency`
 * via the passed FX multiplier. Same rendering rules as `money()`. Callers
 * pass the rate they already fetched via `useFxRate`, so this is a pure
 * function - no per-row network hits inside the render tree.
 */
const inrFrom = (n: number, srcCurrency: string, rate: number): string => {
  if (!Number.isFinite(n)) return '-';
  const converted =
    !srcCurrency || srcCurrency.toUpperCase() === 'INR'
      ? n
      : n * (Number.isFinite(rate) ? rate : 1);
  return money(converted, 'INR');
};

/**
 * Split configured children into adult-priced vs child-priced seats by age.
 * `adultAboveAge` is the age ABOVE which a child pays/needs an adult seat -
 * 8 for transfers (coach seat), 12 for activities. Children with unknown or
 * missing ages fall to the child bucket. The adult-priced kids are added to
 * the adult count; the remainder are charged the child price.
 */
const splitChildrenByAge = (
  children: number,
  childAges: number[],
  adultAboveAge: number,
): { adultKids: number; childKids: number } => {
  const adultKids = Math.min(children, childAges.filter((a) => a > adultAboveAge).length);
  return { adultKids, childKids: Math.max(0, children - adultKids) };
};

/** Age above which a child pays an adult fare. */
const TRANSFER_ADULT_AGE = 8;
const ACTIVITY_ADULT_AGE = 12;

/**
 * Per-mode icon + label for the transfer picker. CAR is per-vehicle; BUS /
 * TRAIN / FERRY are public per-person modes (priced by a fare class / cabin,
 * no vehicle). FERRY here is the point-to-point sea crossing transfer - it
 * never consumes trip-nights (multi-night cruise-as-stay is a separate,
 * hotel-like concept handled elsewhere).
 */
const TRANSFER_MODE_META: Record<TransferMode, { label: string; Icon: typeof Car }> = {
  CAR: { label: 'Car', Icon: Car },
  BUS: { label: 'Bus', Icon: Bus },
  TRAIN: { label: 'Train', Icon: TrainFront },
  FERRY: { label: 'Ferry', Icon: Ship },
};

interface DayItemPickerModalProps {
  cityId: string | null;
  cityName: string | null;
  defaultTime: string;
  busy: boolean;
  /** Which tab to open on (transfer placeholders open straight on Transfers). */
  initialTab?: Tab;
  /** Pre-applied transfer category filter (AIRPORT / INTERCITY / ...). */
  initialCategory?: TransferCategory;
  /** Structural-transfer slot marker stored on the picked transfer's metaJson. */
  transferSlot?: 'a2h' | 'h2a' | 'c2c';
  /** Itinerary pax - drives variant + transfer pricing. */
  adults?: number;
  children?: number;
  /** Per-child ages from the room config. Splits children into child- vs
   *  adult-priced seats: transfers charge >8 as adult, activities charge >12 as
   *  adult (the rest pay the child price). */
  childAges?: number[];
  /**
   * Cost of the component being replaced (paise). When set, leads / agents see
   * a +/- delta vs this baseline — same contract as the flight/hotel picker.
   * Absolute catalog prices stay ADMIN-only.
   */
  currentCostPaise?: string | null;
  /** Long-haul destinations carry no activity/transfer rates: the agent picks
   *  the item as usual, then enters a manual net price (INR) for it. */
  manualPrice?: boolean;
  onClose: () => void;
  onPick: (payload: CreateItineraryComponentRequest) => void;
}

export const DayItemPickerModal = ({
  cityId,
  cityName,
  defaultTime,
  busy,
  initialTab,
  initialCategory,
  transferSlot,
  adults = 2,
  children = 0,
  childAges = [],
  currentCostPaise = null,
  manualPrice = false,
  onClose,
  onPick,
}: DayItemPickerModalProps): React.JSX.Element => {
  // Absolute catalog prices stay ADMIN-only. Leads / agents still see a +/-
  // PriceDelta against `currentCostPaise` when changing an existing item
  // (mirrors the flight / hotel TBO picker).
  const showPrices = useCurrentUser()?.role === 'ADMIN';

  const [tab, setTab] = useState<Tab>(initialTab ?? 'ACTIVITY');
  const [search, setSearch] = useState('');
  // Multi-select: empty array = all categories.
  const [transferCategory, setTransferCategory] = useState<string[]>(
    initialCategory ? [initialCategory] : [],
  );
  // 2026-07-06. Extended filter set based on ActivityListQuerySchema +
  // TransferListQuerySchema. All filters are server-side except `sort`
  // (client-side because neither API supports server sort beyond the
  // shared pagination `sortBy` which isn't wired to aggregated fields
  // like base price).
  const [transferType, setTransferType] = useState<string>('');
  // Transport mode filter (CAR/BUS/TRAIN/CRUISE). Empty = all modes. Applied
  // server-side; the c2c connector opens with this empty so every mode shows.
  const [transferMode, setTransferMode] = useState<string>('');
  const [sort, setSort] = useState<SortKey>('name');
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  // When set, the variant step for that activity is shown instead of the list.
  const [variantFor, setVariantFor] = useState<{ id: string; name: string } | null>(null);
  // When set, the rate/vehicle step for that transfer is shown instead of the list.
  const [transferFor, setTransferFor] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        if (variantFor) setVariantFor(null);
        else if (transferFor) setTransferFor(null);
        else onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, variantFor, transferFor]);

  const term = search.trim();
  const activities = useActivitiesList({
    page: 1,
    pageSize: 100,
    sortDir: 'asc',
    ...(cityId ? { cityId, expandToCountry: true } : {}),
    ...(term ? { search: term } : {}),
  });
  const transfers = useTransfersList({
    page: 1,
    pageSize: 100,
    sortDir: 'asc',
    // Global transfers (isGlobal) always surface here regardless of the day's
    // city; the server still gates them to ACTIVE + approved.
    includeGlobal: true,
    ...(cityId ? { cityId, expandToCountry: true } : {}),
    ...(term ? { search: term } : {}),
    // Category is a multi-select filter applied client-side (see transferRows).
    ...(transferType ? { transferType: transferType as never } : {}),
    ...(transferMode ? { transferMode: transferMode as never } : {}),
  });

  const loading = tab === 'ACTIVITY' ? activities.isLoading : transfers.isLoading;

  // 2026-07-06. Client-side sort. Sort keys beyond `name` aren't natively
  // supported by the list endpoints yet (see SortKey comment above), so we
  // sort the current page in memory. Result set is capped at pageSize=100
  // above, so this is O(100 log 100) - imperceptible.
  const activityRows = useMemo(() => {
    const rows = activities.data?.data ?? [];
    return applySort(
      rows,
      sort,
      (a) => a.baseAdultPrice,
      (a) => a.rating,
      (a) => a.createdAt,
    );
  }, [activities.data, sort]);
  const transferRows = useMemo(() => {
    let rows = transfers.data?.data ?? [];
    if (transferCategory.length > 0) {
      // Public modes (bus/train/cruise) carry no sub-category, so they bypass
      // the category filter - a train is the natural fill for the city-to-city
      // slot even though it isn't tagged AIRPORT/HOTEL/INTERCITY.
      rows = rows.filter(
        (t) =>
          isPublicTransferMode(t.transferMode) ||
          (t.transferCategory && transferCategory.includes(t.transferCategory)),
      );
    }
    return applySort(
      rows,
      sort,
      (t) => t.baseRatePrice,
      () => 0,
      (t) => t.createdAt,
    );
  }, [transfers.data, sort, transferCategory]);

  const addTransfer = (payload: CreateItineraryComponentRequest): void => {
    // The rate/vehicle step computed cost + chose the rate; stamp the
    // structural slot marker (a2h / h2a / c2c) onto metaJson if present.
    onPick({
      ...payload,
      ...(transferSlot
        ? {
            metaJson: {
              ...((payload.metaJson as Record<string, unknown> | null) ?? {}),
              slot: transferSlot,
            },
          }
        : {}),
    });
    if (payload.transferId) setAddedIds((s) => new Set(s).add(payload.transferId as string));
    setTransferFor(null);
    onClose();
  };
  const addVariant = (payload: CreateItineraryComponentRequest): void => {
    onPick(payload);
    if (payload.activityId) setAddedIds((s) => new Set(s).add(payload.activityId as string));
    setVariantFor(null);
    // User flow: pick activity -> confirm in variant panel -> Add closes
    // both the variant panel and the outer modal in one click.
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex h-[84vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-center justify-between border-b border-zinc-100 px-5 py-3.5">
          <div className="flex items-center gap-2">
            {(variantFor || transferFor) && (
              <button
                type="button"
                onClick={() => {
                  setVariantFor(null);
                  setTransferFor(null);
                }}
                className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
                aria-label="Back to list"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            <div>
              <h3 className="text-sm font-semibold tracking-tight text-zinc-900">
                {variantFor ? variantFor.name : transferFor ? transferFor.name : 'Add to day'}
              </h3>
              {cityName && (
                <p className="mt-0.5 flex items-center gap-1 text-xs text-zinc-500">
                  <MapPin className="h-3 w-3" aria-hidden />
                  {cityName}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {variantFor ? (
          <ActivityVariantPanel
            activityId={variantFor.id}
            defaultTime={defaultTime}
            adults={adults}
            children={children}
            childAges={childAges}
            busy={busy}
            showPrices={showPrices}
            manualPrice={manualPrice}
            currentCostPaise={currentCostPaise}
            onAdd={addVariant}
          />
        ) : transferFor ? (
          <TransferVariantPanel
            transferId={transferFor.id}
            transferName={transferFor.name}
            defaultTime={defaultTime}
            adults={adults}
            children={children}
            childAges={childAges}
            busy={busy}
            showPrices={showPrices}
            manualPrice={manualPrice}
            currentCostPaise={currentCostPaise}
            onAdd={addTransfer}
          />
        ) : (
          <div className="flex min-h-0 flex-1">
            {/* Left filter rail */}
            <aside className="hidden w-48 shrink-0 space-y-4 border-r border-zinc-100 p-4 md:block">
              <div className="space-y-1">
                <p className="px-1 text-[10px] font-semibold tracking-wider text-zinc-400 uppercase">
                  Type
                </p>
                {(['ACTIVITY', 'TRANSFER'] as Tab[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTab(t)}
                    className={
                      'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-sm transition-colors ' +
                      (tab === t
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-accent hover:text-accent-foreground text-zinc-700')
                    }
                  >
                    {t === 'ACTIVITY' ? (
                      <Ticket className="h-3.5 w-3.5" aria-hidden />
                    ) : (
                      <Car className="h-3.5 w-3.5" aria-hidden />
                    )}
                    {t === 'ACTIVITY' ? 'Activities' : 'Transfers'}
                  </button>
                ))}
              </div>

              {tab === 'TRANSFER' && (
                <>
                  {/* Transport mode (Car / Bus / Train / Cruise). The
                      city-to-city connector opens with this empty so the
                      operator can pick any mode for the leg. */}
                  <UzSelect
                    label="Mode"
                    value={transferMode}
                    onValueChange={setTransferMode}
                    options={[
                      { value: '', label: 'Any mode' },
                      ...TransferModeSchema.options.map((v) => ({
                        value: v,
                        label: TRANSFER_MODE_META[v].label,
                      })),
                    ]}
                  />
                  {/* 2026-07-06. Transfer type (ONE_WAY / ROUND_TRIP)
                      is a first-class column on the transfers table -
                      operators need it when picking airport transfers
                      that come in both flavors and want the right
                      priced rate up-front. */}
                  <UzSelect
                    label="Trip type"
                    value={transferType}
                    onValueChange={setTransferType}
                    options={[
                      { value: '', label: 'Any trip type' },
                      ...TransferTypeSchema.options.map((v) => ({
                        value: v,
                        label: titleCase(v),
                      })),
                    ]}
                  />
                </>
              )}

              {/* 2026-07-06. Sort is client-side over the current page (100
                  rows cap). Shared by both tabs. */}
              <UzSelect
                label="Sort by"
                value={sort}
                onValueChange={(v) => setSort((v as SortKey) ?? 'name')}
                options={SORT_OPTIONS.filter(
                  (o) => showPrices || (o.value !== 'price-asc' && o.value !== 'price-desc'),
                ).map((o) => ({ value: o.value, label: o.label }))}
              />

              {(search ||
                transferCategory.length > 0 ||
                transferType ||
                transferMode ||
                sort !== 'name') && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setTransferCategory([]);
                    setTransferType('');
                    setTransferMode('');
                    setSort('name');
                  }}
                  className="inline-flex w-full items-center justify-center gap-1 rounded-md border border-zinc-200 px-2 py-1.5 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                >
                  <RotateCcw className="h-3 w-3" aria-hidden />
                  Reset filters
                </button>
              )}

              {!cityId && (
                <p className="rounded-md bg-amber-50 px-2 py-1.5 text-[11px] text-amber-700">
                  This day has no city set, so results aren&apos;t scoped.
                </p>
              )}
            </aside>

            {/* Right: search + results */}
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="border-b border-zinc-100 p-4">
                <UzInput
                  placeholder={`Search ${tab === 'ACTIVITY' ? 'activities' : 'transfers'}...`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {tab === 'TRANSFER' && (
                <div className="flex flex-wrap items-center gap-2 border-b border-zinc-100 px-4 py-3">
                  <span className="mr-1 text-xs font-medium text-zinc-500">Category</span>
                  {TransferCategorySchema.options.map((v) => {
                    const active = transferCategory.includes(v);
                    return (
                      <button
                        key={v}
                        type="button"
                        onClick={() =>
                          setTransferCategory((prev) =>
                            active ? prev.filter((c) => c !== v) : [...prev, v],
                          )
                        }
                        className={
                          'rounded-full border px-3 py-1 text-xs font-medium transition-colors ' +
                          (active
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50')
                        }
                      >
                        {titleCase(v)}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="min-h-0 flex-1 overflow-auto p-4">
                {loading ? (
                  <div className="flex items-center justify-center gap-2 py-10 text-sm text-zinc-400">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    Loading...
                  </div>
                ) : tab === 'ACTIVITY' ? (
                  <ResultList
                    rows={activityRows.map((a) => ({
                      id: a.id,
                      name: a.name,
                      sub: [a.category, a.cityName].filter(Boolean).join(' · '),
                      meta: a.variantCount > 1 ? `${a.variantCount} variants` : undefined,
                      imageUrl: a.imageUrl,
                      priceInr: a.baseAdultPriceInr,
                      priceQualifier: 'per adult',
                    }))}
                    header={
                      activities.data
                        ? `${activityRows.length} of ${activities.data.pagination.totalItems} activit${activities.data.pagination.totalItems === 1 ? 'y' : 'ies'}`
                        : null
                    }
                    addedIds={addedIds}
                    busy={busy}
                    showPrices={showPrices}
                    currentCostPaise={currentCostPaise}
                    Icon={Ticket}
                    actionLabel="Choose"
                    onAction={(id, name) => setVariantFor({ id, name })}
                    empty="No activities found for this city."
                  />
                ) : (
                  <ResultList
                    rows={transferRows.map((t) => ({
                      id: t.id,
                      name: t.name,
                      sub: [
                        TRANSFER_MODE_META[t.transferMode].label,
                        t.operatorName,
                        titleCase(t.transferType),
                        t.rateCount > 0
                          ? `${t.rateCount} rate${t.rateCount === 1 ? '' : 's'}`
                          : null,
                        t.cityName,
                      ]
                        .filter(Boolean)
                        .join(' · '),
                      imageUrl: t.imageUrl,
                      RowIcon: TRANSFER_MODE_META[t.transferMode].Icon,
                      priceInr: t.baseRatePriceInr,
                      priceQualifier: isPublicTransferMode(t.transferMode)
                        ? 'per person'
                        : t.baseRateServiceType === 'SIC'
                          ? 'per person'
                          : t.baseRateServiceType === 'PRIVATE'
                            ? 'per vehicle'
                            : null,
                      ...(t.isMultiDayPass ? { badge: 'Multi-day pass' } : {}),
                    }))}
                    header={
                      transfers.data
                        ? `${transferRows.length} of ${transfers.data.pagination.totalItems} transfer${transfers.data.pagination.totalItems === 1 ? '' : 's'}`
                        : null
                    }
                    addedIds={addedIds}
                    busy={busy}
                    showPrices={showPrices}
                    currentCostPaise={currentCostPaise}
                    Icon={Car}
                    actionLabel="Choose"
                    onAction={(id, name) => setTransferFor({ id, name })}
                    empty="No transfers found for this city."
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---- Variant step ----------------------------------------------------------

const ActivityVariantPanel = ({
  activityId,
  defaultTime,
  adults,
  children,
  childAges,
  busy,
  showPrices,
  manualPrice,
  currentCostPaise,
  onAdd,
}: {
  activityId: string;
  defaultTime: string;
  adults: number;
  children: number;
  childAges: number[];
  busy: boolean;
  /** ADMIN-only: whether to render sales prices/totals (see modal root). */
  showPrices: boolean;
  /** Long-haul: agent enters the net price instead of using the (zero) master. */
  manualPrice: boolean;
  currentCostPaise?: string | null;
  onAdd: (payload: CreateItineraryComponentRequest) => void;
}): React.JSX.Element => {
  const detailQ = useActivityDetail(activityId);
  const a = detailQ.data;
  const [variantId, setVariantId] = useState<string | null>(null);
  const [transferMode, setTransferMode] = useState<'NONE' | 'SIC' | 'PRIVATE'>('NONE');

  const cur = a?.currencyCode ?? 'INR';
  const pax = Math.max(1, adults) + Math.max(0, children);
  // Activities charge children over 12 the adult fare (ticket + SIC add-on).
  const { adultKids, childKids } = splitChildrenByAge(children, childAges, ACTIVITY_ADULT_AGE);
  const billAdults = adults + adultKids;

  // Occupancy-tiered private combos (e.g. VinWonders / Singapore PVT) store one
  // per-head variant per EXACT adult count (minPax === maxPax, no fixedCost):
  // "1 Adult", "2 Adults", ... The picker narrows to the tier matching the
  // adult count and labels it by adults, appending the trip's children so the
  // seller + customer read "2 Adults + 1 Child" rather than a bare "2 Pax".
  const isExactAdultTier = (v: {
    fixedCost?: number | null;
    minPax?: number | null;
    maxPax?: number | null;
  }): boolean => (v.fixedCost ?? 0) === 0 && v.minPax != null && v.minPax === v.maxPax;
  const tierLabel = (v: {
    name: string;
    fixedCost?: number | null;
    minPax?: number | null;
    maxPax?: number | null;
  }): string => {
    if (!isExactAdultTier(v)) return v.name;
    const adultPart = `${v.minPax} Adult${v.minPax === 1 ? '' : 's'}`;
    return children > 0
      ? `${adultPart} + ${children} Child${children === 1 ? '' : 'ren'}`
      : adultPart;
  };
  // 2026-07-07. Every price on this panel renders in INR - the itinerary
  // currency the operator quotes in. The multiplier comes from the same
  // `CurrencyConversionService` the API uses server-side when the picked
  // component is saved, so preview + saved amounts stay consistent.
  const fx = useFxRate(cur, 'INR');
  const rate = fx.data?.rate ?? 1;
  const inr = (n: number): string => inrFrom(n, cur, rate);

  // Visible tiers. Two pricing shapes are handled distinctly:
  //   - Pax-slab (fixed_cost) variants: priced as a flat lump-sum for an exact
  //     [minPax, maxPax] window, with adult/child price = 0. Only the slab that
  //     MATCHES the itinerary pax is shown, so a 2-pax trip lands on "2 Pax",
  //     not the base "1 Pax". (These were hidden entirely before - the old
  //     price filter kept only `isBase || adultPrice>0 || childPrice>0`, and a
  //     fixed-cost slab is none of those unless it is the base tier.)
  //   - Per-head variants: base entry always shows; extra tiers only when they
  //     carry a real adult/child price (hide 0-priced ones per the brief).
  //   - Long-haul masters are all 0-priced - show everything and let the agent
  //     price it.
  const variants = useMemo(() => {
    if (!a) return [];
    const sorted = [...a.variants].sort(
      (x, y) => (y.isBase ? 1 : 0) - (x.isBase ? 1 : 0) || x.sortOrder - y.sortOrder,
    );
    if (manualPrice) return sorted;

    const hasExactAdultTiers = sorted.some((v) => isExactAdultTier(v));
    const filtered = sorted.filter((v) => {
      if ((v.fixedCost ?? 0) > 0) {
        return pax >= (v.minPax ?? 1) && pax <= (v.maxPax ?? Number.POSITIVE_INFINITY);
      }
      // Exact per-adult occupancy tier: show only the one for this adult count
      // (like the fixed-cost slabs, but keyed on ADULTS - children are added on
      // top at the child rate, so they don't move the tier).
      if (isExactAdultTier(v)) {
        return adults >= (v.minPax ?? 1) && adults <= (v.maxPax ?? Number.POSITIVE_INFINITY);
      }
      return v.isBase || v.adultPrice > 0 || v.childPrice > 0;
    });

    // Never leave the picker blank: if the adult/pax count falls outside every
    // tier (e.g. a group larger than the biggest tier), fall back to showing all
    // the slabs / adult tiers so the operator can still pick the closest.
    if (filtered.length === 0 && sorted.some((v) => (v.fixedCost ?? 0) > 0)) {
      return sorted.filter((v) => (v.fixedCost ?? 0) > 0);
    }
    if (filtered.length === 0 && hasExactAdultTiers) {
      return sorted.filter((v) => isExactAdultTier(v));
    }
    return filtered;
  }, [a, manualPrice, pax, adults]);

  useEffect(() => {
    if (variants.length === 0) return;
    if (!variantId || !variants.some((v) => v.id === variantId)) {
      setVariantId(variants.find((v) => v.isBase)?.id ?? variants[0]?.id ?? null);
    }
  }, [variants, variantId]);

  const variant = variants.find((v) => v.id === variantId) ?? null;
  const includesTransfer = variant?.transferIncluded ?? false;

  // A separate transfer add-on is offered only when the tier doesn't already
  // bundle one, and only for the modes that have a non-zero rate configured.
  const sicAvail = !!a && ((a.sicAdultPrice ?? 0) > 0 || (a.sicChildPrice ?? 0) > 0);
  const privateAvail =
    !!a && ((a.privateUpto4 ?? 0) > 0 || (a.privateUpto7 ?? 0) > 0 || (a.privateUpto12 ?? 0) > 0);

  useEffect(() => {
    if (includesTransfer && transferMode !== 'NONE') setTransferMode('NONE');
    else if (transferMode === 'SIC' && !sicAvail) setTransferMode('NONE');
    else if (transferMode === 'PRIVATE' && !privateAvail) setTransferMode('NONE');
  }, [includesTransfer, sicAvail, privateAvail, transferMode]);

  const privateRate = (): number => {
    if (!a) return 0;
    if (pax <= 4) return a.privateUpto4 ?? a.privateUpto7 ?? a.privateUpto12 ?? 0;
    if (pax <= 7) return a.privateUpto7 ?? a.privateUpto12 ?? 0;
    return a.privateUpto12 ?? 0;
  };

  // Legacy `FixedCost > 0` semantic: when the variant is set to a flat lump-sum,
  // use it regardless of pax count (typically within [minPax, maxPax]). Otherwise
  // fall back to per-pax pricing. Kept the fixed-cost check independent of the
  // pax window - if the operator set fixed_cost, they meant it.
  const ticketCost = variant
    ? variant.fixedCost !== null && variant.fixedCost !== undefined && variant.fixedCost > 0
      ? variant.fixedCost
      : // Children over 12 pay the adult ticket; the rest pay child price as-is.
        variant.adultPrice * billAdults + variant.childPrice * childKids
    : 0;
  const transferCost = (() => {
    if (includesTransfer || transferMode === 'NONE' || !a) return 0;
    if (transferMode === 'SIC') {
      // SIC add-on is a seat: over-12 pay adult; child price 0/null -> adult.
      const sicAdult = a.sicAdultPrice ?? 0;
      const sicChild = a.sicChildPrice && a.sicChildPrice > 0 ? a.sicChildPrice : sicAdult;
      return sicAdult * billAdults + sicChild * childKids;
    }
    if (transferMode === 'PRIVATE') return privateRate();
    return 0;
  })();
  const total = ticketCost + transferCost;
  // Long-haul stores the price in INR (server FX no-ops on 'INR'), so pre-convert
  // the computed total to INR paise for the auto-filled net price.
  const toInrPaise = (n: number): number =>
    Math.max(0, Math.round((cur.toUpperCase() === 'INR' ? n : n * rate) * 100));
  const effectiveMode: ItineraryActivityTransferMode = includesTransfer ? 'INCLUDED' : transferMode;
  const showTransferSection = !includesTransfer && (sicAvail || privateAvail);

  const add = (): void => {
    if (!a || !variant) return;
    // Prefer the master's scheduled clock-times so the card shows the real
    // activity window. Fall back to the caller's defaultTime when the master
    // has nothing set. End time stays null when only a start is known - the
    // card concatenates whichever is present.
    const startTime = a.scheduleStartTime ?? a.pickupTime ?? defaultTime;
    const endTime = a.scheduleEndTime ?? null;
    onAdd({
      kind: 'ACTIVITY',
      source: 'DB',
      // Exact per-adult tiers always carry their occupancy label (even the "1
      // Adult" base) since it names who the price covers; other activities keep
      // the "name only for non-base tier" convention.
      title: isExactAdultTier(variant)
        ? `${a.name} - ${tierLabel(variant)}`
        : `${a.name}${variant.isBase ? '' : ` - ${variant.name}`}`,
      activityId: a.id,
      activityVariantId: variant.id,
      activityTransferMode: effectiveMode,
      startTime,
      ...(endTime ? { endTime } : {}),
      ...(a.pickupPoint ? { fromLocation: a.pickupPoint } : {}),
      ...(a.dropOffPoint ? { toLocation: a.dropOffPoint } : {}),
      // Long-haul: the net price is auto-filled from the computed total (adult +
      // child calc), converted to INR, and the agent can edit it on the Days row.
      // A rate-less master computes 0 - it keeps the prior manual entry on
      // re-pick, else lands empty for the agent to type. INR is stamped so the
      // server FX no-ops. Normal (short-haul): computed total in master currency.
      costPaise: manualPrice
        ? toInrPaise(total) > 0
          ? String(toInrPaise(total))
          : (currentCostPaise ?? '0')
        : String(Math.max(0, Math.round(total * 100))),
      metaJson: {
        currencyCode: manualPrice ? 'INR' : cur,
        variantName: tierLabel(variant),
        adultPrice: variant.adultPrice,
        childPrice: variant.childPrice,
        adults,
        children,
        categoryName: a.category ?? null,
        tourDurationMinutes: a.tourDurationMinutes,
        // Pax window is per-variant now (ActivityVariant.minPax/maxPax).
        minimumPaxCount: variant.minPax,
        maximumPaxCount: variant.maxPax,
        pickupLocationName: a.pickupPoint ?? null,
        dropOffLocationName: a.dropOffPoint ?? null,
        coverPhotoUrl: a.imageUrl ?? null,
        transfer:
          effectiveMode === 'NONE'
            ? null
            : {
                mode: effectiveMode,
                type: includesTransfer ? variant.transferType : transferMode,
                direction: includesTransfer ? variant.transferDirection : 'BOTH',
                costMinor: Math.round(transferCost * 100),
              },
      },
    });
  };

  if (detailQ.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 text-sm text-zinc-400">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading variants...
      </div>
    );
  }
  if (detailQ.isError || !a) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-red-600">
        Failed to load this activity. Go back and retry.
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-5 overflow-auto p-5">
        {/* Ticket tiers */}
        <section>
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-xs font-semibold tracking-wider text-zinc-500 uppercase">
              Ticket variant
            </h4>
            <span className="inline-flex items-center gap-1 text-[11px] text-zinc-400">
              <Users className="h-3 w-3" aria-hidden />
              {adults} adult{adults === 1 ? '' : 's'}
              {children > 0 ? ` · ${children} child${children === 1 ? '' : 'ren'}` : ''}
            </span>
          </div>
          <div className="space-y-2">
            {variants.map((v) => {
              const selected = v.id === variantId;
              const isFixed = v.fixedCost !== null && v.fixedCost !== undefined && v.fixedCost > 0;
              const paxWindow =
                v.minPax || v.maxPax ? ` · for ${v.minPax ?? 1}-${v.maxPax ?? '∞'} pax` : '';
              const sub = isFixed
                ? `Flat ${inr(v.fixedCost as number)}${paxWindow}`
                : `${inr(v.adultPrice)} / adult${
                    v.childPrice > 0 ? ` · ${inr(v.childPrice)} / child` : ''
                  }`;
              const rowTotal = isFixed
                ? (v.fixedCost as number)
                : v.adultPrice * adults + v.childPrice * children;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setVariantId(v.id)}
                  className={
                    'flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ' +
                    (selected
                      ? 'border-primary bg-primary/5 ring-primary/20 ring-1'
                      : 'border-zinc-200 hover:border-zinc-300')
                  }
                >
                  <span
                    className={
                      'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ' +
                      (selected ? 'border-primary bg-primary text-white' : 'border-zinc-300')
                    }
                  >
                    {selected && <Check className="h-3 w-3" aria-hidden />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start gap-2">
                      <span className="min-w-0 flex-1 text-sm font-medium break-words text-zinc-900">
                        {tierLabel(v)}
                      </span>
                      {v.isBase && !isExactAdultTier(v) && (
                        <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-500">
                          Base
                        </span>
                      )}
                      {v.transferIncluded && (
                        <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                          <Bus className="h-2.5 w-2.5" aria-hidden />
                          {v.transferType === 'PRIVATE' ? 'Private' : 'SIC'} transfer incl.
                        </span>
                      )}
                    </div>
                    {showPrices && sub && (
                      <p className="mt-0.5 text-xs text-zinc-500 tabular-nums">{sub}</p>
                    )}
                  </div>
                  {showPrices && (
                    <span className="shrink-0 text-sm font-semibold text-zinc-900 tabular-nums">
                      {inr(rowTotal)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* Transfer add-on (only when the tier doesn't bundle one) */}
        {showTransferSection && (
          <section>
            <h4 className="mb-2 text-xs font-semibold tracking-wider text-zinc-500 uppercase">
              Transfer
            </h4>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              <TransferOption
                label="None"
                price="Ticket only"
                Icon={Ticket}
                selected={transferMode === 'NONE'}
                onClick={() => setTransferMode('NONE')}
              />
              {sicAvail && (
                <TransferOption
                  label="SIC"
                  price={
                    showPrices
                      ? inr(
                          (a.sicAdultPrice ?? 0) * billAdults +
                            (a.sicChildPrice && a.sicChildPrice > 0
                              ? a.sicChildPrice
                              : (a.sicAdultPrice ?? 0)) *
                              childKids,
                        )
                      : ''
                  }
                  Icon={Bus}
                  selected={transferMode === 'SIC'}
                  onClick={() => setTransferMode('SIC')}
                />
              )}
              {privateAvail && (
                <TransferOption
                  label="Private"
                  price={showPrices ? inr(privateRate()) : ''}
                  Icon={Car}
                  selected={transferMode === 'PRIVATE'}
                  onClick={() => setTransferMode('PRIVATE')}
                />
              )}
            </div>
          </section>
        )}

        {includesTransfer && variant && (
          <p className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
            <Bus className="h-3.5 w-3.5" aria-hidden />
            This variant already includes a{' '}
            {variant.transferType === 'PRIVATE' ? 'private' : 'seat-in-coach'} transfer
            {variant.transferDirection ? ` (${titleCase(variant.transferDirection)})` : ''}.
          </p>
        )}
      </div>

      {/* Footer: total + add.
       *
       * Long-haul (`manualPrice`) no longer collects the net price inline -
       * the agent picks the activity and confirms the pax config here, and
       * the actual net price is entered on the day row after the component
       * is dropped in. Zero-priced master variants therefore result in a
       * component with `costPaise = 0`, which the Days tab renders as an
       * inline "Save price" input on the row. The `+ Add` button no longer
       * requires a manual amount for long-haul; it's enabled as soon as a
       * variant is selected. */}
      <div className="flex items-center justify-between gap-3 border-t border-zinc-100 px-5 py-3.5">
        <div>
          {manualPrice ? (
            <div className="max-w-xs">
              <p className="text-[11px] tracking-wider text-zinc-400 uppercase">
                Long-haul destination
              </p>
              <p className="mt-0.5 text-xs text-zinc-500">
                Net price is auto-filled from the fare where set. Adjust it on the day row.
              </p>
            </div>
          ) : showPrices ? (
            <>
              <p className="text-[11px] text-zinc-400">Total for {pax} pax</p>
              <div className="flex items-baseline gap-2">
                <p className="text-base font-semibold text-zinc-900 tabular-nums">{inr(total)}</p>
                <PriceDelta
                  base={currentCostPaise}
                  now={String(Math.max(0, Math.round(total * 100)))}
                  currency="INR"
                />
              </div>
            </>
          ) : (
            <div className="space-y-1">
              <p className="text-[11px] text-zinc-400">
                {pax} pax · {adults} adult{adults === 1 ? '' : 's'}
                {children > 0 ? ` · ${children} child${children === 1 ? '' : 'ren'}` : ''}
              </p>
              <PriceDelta
                base={currentCostPaise}
                now={String(Math.max(0, Math.round(total * 100)))}
                currency="INR"
              />
            </div>
          )}
        </div>
        <UzButton onClick={add} loading={busy} disabled={!variant}>
          <Plus className="mr-1.5 h-4 w-4" aria-hidden />
          Add to day
        </UzButton>
      </div>
    </div>
  );
};

const TransferOption = ({
  label,
  price,
  Icon,
  selected,
  onClick,
}: {
  label: string;
  price: string;
  Icon: typeof Bus;
  selected: boolean;
  onClick: () => void;
}): React.JSX.Element => (
  <button
    type="button"
    onClick={onClick}
    className={
      'flex flex-col items-start gap-1 rounded-lg border p-2.5 text-left transition-colors ' +
      (selected
        ? 'border-primary bg-primary/5 ring-primary/20 ring-1'
        : 'border-zinc-200 hover:border-zinc-300')
    }
  >
    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-900">
      <Icon className="h-3.5 w-3.5 text-zinc-400" aria-hidden />
      {label}
    </span>
    {price && <span className="text-xs text-zinc-500 tabular-nums">{price}</span>}
  </button>
);

// ---- Transfer rate/vehicle step -------------------------------------------

/**
 * Standalone-transfer step: choose the service type (Private / SIC) and, for
 * Private, the vehicle tier + count. Private auto-suggests the cheapest single
 * tier for the day's pax (vehicles = ceil(pax / tier capacity)); every field
 * stays editable. Cost is the catalog rate (per-vehicle × count, or per-person
 * × pax) - consistent with activities; markup is applied at the quote step.
 */
const TransferVariantPanel = ({
  transferId,
  transferName,
  defaultTime,
  adults,
  children,
  childAges,
  busy,
  showPrices,
  manualPrice,
  currentCostPaise,
  onAdd,
}: {
  transferId: string;
  transferName: string;
  defaultTime: string;
  adults: number;
  children: number;
  childAges: number[];
  busy: boolean;
  /** ADMIN-only: whether to render sales prices/totals (see modal root). */
  showPrices: boolean;
  /** Long-haul: agent enters the net price instead of using the (zero) master. */
  manualPrice: boolean;
  currentCostPaise?: string | null;
  onAdd: (payload: CreateItineraryComponentRequest) => void;
}): React.JSX.Element => {
  const detailQ = useTransferDetail(transferId);
  const t = detailQ.data;
  const cur = t?.currencyCode ?? 'INR';
  const pax = Math.max(1, adults) + Math.max(0, children);
  // Transfers charge children over 8 the adult seat fare (coach seat).
  const { adultKids, childKids } = splitChildrenByAge(children, childAges, TRANSFER_ADULT_AGE);
  const seatAdults = adults + adultKids;
  // 2026-07-07. Every price on this panel renders in INR (see the sister
  // block in `ActivityVariantPanel`).
  const fx = useFxRate(cur, 'INR');
  const rate = fx.data?.rate ?? 1;
  const inr = (n: number): string => inrFrom(n, cur, rate);
  // Long-haul stores the price in INR (server FX no-ops on 'INR'), so pre-convert
  // the computed cost to INR paise for the auto-filled net price.
  const toInrPaise = (n: number): number =>
    Math.max(0, Math.round((cur.toUpperCase() === 'INR' ? n : n * rate) * 100));

  // CAR is the only per-vehicle mode; BUS / TRAIN / CRUISE are public and
  // per-person (all rates stored as SIC, distinguished by a fare class / cabin).
  const transferMode: TransferMode = t?.transferMode ?? 'CAR';
  const isPublic = isPublicTransferMode(transferMode);
  const modeMeta = TRANSFER_MODE_META[transferMode];

  const privateRates = useMemo(
    () =>
      (t?.rates ?? [])
        .filter((r) => r.serviceType === 'PRIVATE')
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [t],
  );
  // All SIC rates, sorted. CAR uses the first as its single shared option; a
  // public mode surfaces every one as a selectable fare class / cabin.
  const sicRates = useMemo(
    () =>
      (t?.rates ?? [])
        .filter((r) => r.serviceType === 'SIC')
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [t],
  );
  const sicRate = sicRates[0] ?? null;
  const hasPrivate = privateRates.length > 0;
  const hasSic = sicRates.length > 0;

  const [mode, setMode] = useState<'PRIVATE' | 'SIC'>('PRIVATE');
  // Default to the base rate's service type once the detail loads.
  useEffect(() => {
    if (!t) return;
    const base = t.rates.find((r) => r.isBase);
    setMode(base?.serviceType === 'SIC' || (!hasPrivate && hasSic) ? 'SIC' : 'PRIVATE');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);
  // Keep the mode on an available option (CAR only; public is always SIC).
  useEffect(() => {
    if (isPublic) return;
    if (mode === 'PRIVATE' && !hasPrivate && hasSic) setMode('SIC');
    else if (mode === 'SIC' && !hasSic && hasPrivate) setMode('PRIVATE');
  }, [mode, hasPrivate, hasSic, isPublic]);

  // Private auto-suggest: cheapest single-tier plan for this pax count.
  const suggestion = useMemo(() => {
    let best: { rateId: string; vehicles: number; cost: number } | null = null;
    for (const r of privateRates) {
      const cap = Math.max(1, r.paxMax);
      const vehicles = Math.max(1, Math.ceil(pax / cap));
      const cost = vehicles * r.pricePerVehicle;
      if (!best || cost < best.cost) best = { rateId: r.id, vehicles, cost };
    }
    return best;
  }, [privateRates, pax]);

  const [rateId, setRateId] = useState<string | null>(null);
  const [vehicles, setVehicles] = useState<number>(1);
  // Sync the chosen tier + vehicle count to the auto-suggestion (CAR / PRIVATE).
  useEffect(() => {
    if (!isPublic && mode === 'PRIVATE' && suggestion) {
      setRateId(suggestion.rateId);
      setVehicles(suggestion.vehicles);
    }
  }, [isPublic, mode, suggestion]);

  // Public modes: the selected fare class / cabin. Defaults to the base SIC rate.
  const [fareRateId, setFareRateId] = useState<string | null>(null);
  useEffect(() => {
    if (!t || !isPublic) return;
    const base = sicRates.find((r) => r.isBase) ?? sicRates[0] ?? null;
    setFareRateId(base?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, isPublic]);

  // Per-seat cost for a SIC/public rate: adults + over-8 kids pay adult; 2-8
  // kids pay the child price (0/null -> adult).
  const perSeatCost = (r: TransferDetail['rates'][number] | null): number => {
    if (!r) return 0;
    const childSeat =
      r.perPersonChildPrice && r.perPersonChildPrice > 0 ? r.perPersonChildPrice : r.perPersonPrice;
    return r.perPersonPrice * seatAdults + childSeat * childKids;
  };

  const chosenPrivate = privateRates.find((r) => r.id === rateId) ?? null;
  const chosenFare = sicRates.find((r) => r.id === fareRateId) ?? null;

  // PRIVATE = per whole vehicle x count, regardless of head count. SIC / public
  // = per seat.
  const cost = isPublic
    ? perSeatCost(chosenFare)
    : mode === 'SIC'
      ? perSeatCost(sicRate)
      : (chosenPrivate?.pricePerVehicle ?? 0) * Math.max(1, vehicles);
  const selectedRate = isPublic ? chosenFare : mode === 'SIC' ? sicRate : chosenPrivate;

  const add = (): void => {
    if (!t || !selectedRate) return;
    const startTime = t.scheduleStartTime ?? t.pickupTime ?? defaultTime;
    onAdd({
      kind: 'TRANSFER',
      source: 'DB',
      title: transferName,
      transferId: t.id,
      transferRateId: selectedRate.id,
      startTime,
      ...(t.fromLocationName ? { fromLocation: t.fromLocationName } : {}),
      ...(t.toLocationName ? { toLocation: t.toLocationName } : {}),
      // Long-haul: the net price is auto-filled from the computed cost (per-person
      // or per-vehicle calc), converted to INR, and the agent can edit it on the
      // Days row. A rate-less master computes 0 - it keeps the prior manual entry
      // on re-pick, else lands empty for the agent to type. INR is stamped so the
      // server FX no-ops. Normal (short-haul): computed cost in master currency.
      costPaise: manualPrice
        ? toInrPaise(cost) > 0
          ? String(toInrPaise(cost))
          : (currentCostPaise ?? '0')
        : String(Math.max(0, Math.round(cost * 100))),
      metaJson: {
        currencyCode: manualPrice ? 'INR' : cur,
        // Public modes are per-person; stored as SIC so downstream per-person
        // math (markup/FX/child seats) flows through unchanged.
        serviceType: isPublic ? 'SIC' : mode,
        // Multi-modal descriptors - drive mode-aware display + the quote line.
        transferMode,
        operatorName: isPublic ? (t.operatorName ?? null) : null,
        fareClassName: isPublic ? (selectedRate.fareClassName ?? selectedRate.label ?? null) : null,
        isMultiDayPass: t.isMultiDayPass ?? false,
        validityDays: t.isMultiDayPass ? (selectedRate.validityDays ?? null) : null,
        vesselOrServiceName: isPublic ? (t.vesselOrServiceName ?? null) : null,
        vehicleTypeName:
          !isPublic && mode === 'PRIVATE'
            ? (selectedRate.vehicleTypeName ?? selectedRate.label)
            : null,
        vehicleCount: !isPublic && mode === 'PRIVATE' ? Math.max(1, vehicles) : null,
        pricePerVehicle: !isPublic && mode === 'PRIVATE' ? selectedRate.pricePerVehicle : null,
        perPersonPrice: isPublic || mode === 'SIC' ? selectedRate.perPersonPrice : null,
        adults,
        children,
        pax,
        transferType: t.transferType,
        transferCategory: t.transferCategory ?? null,
        cityName: t.cityName ?? null,
        fromLocationName: t.fromLocationName ?? null,
        toLocationName: t.toLocationName ?? null,
        coverPhotoUrl: t.imageUrl ?? null,
      },
    });
  };

  if (detailQ.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 text-sm text-zinc-400">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading rates...
      </div>
    );
  }
  if (detailQ.isError || !t) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-red-600">
        Failed to load this transfer. Go back and retry.
      </div>
    );
  }
  if (!hasPrivate && !hasSic) {
    return (
      <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-zinc-400">
        This transfer has no priced rates configured.
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-5 overflow-auto p-5">
        {isPublic ? (
          // Public modes (BUS / TRAIN / CRUISE): per-person, priced by a fare
          // class / cabin. No service-type toggle, no vehicle counter.
          <>
            <section>
              <div className="mb-2 flex items-center justify-between">
                <h4 className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wider text-zinc-500 uppercase">
                  <modeMeta.Icon className="h-3.5 w-3.5" aria-hidden />
                  {modeMeta.label}
                </h4>
                <span className="inline-flex items-center gap-1 text-[11px] text-zinc-400">
                  <Users className="h-3 w-3" aria-hidden />
                  {adults} adult{adults === 1 ? '' : 's'}
                  {children > 0 ? ` · ${children} child${children === 1 ? '' : 'ren'}` : ''}
                </span>
              </div>
              {(t.operatorName || t.vesselOrServiceName) && (
                <p className="text-xs text-zinc-500">
                  {[t.operatorName, t.vesselOrServiceName].filter(Boolean).join(' · ')}
                </p>
              )}
            </section>

            <section className="space-y-3">
              <h4 className="text-xs font-semibold tracking-wider text-zinc-500 uppercase">
                {t.isMultiDayPass
                  ? 'Pass class & validity'
                  : transferMode === 'FERRY'
                    ? 'Cabin'
                    : 'Fare class'}
              </h4>
              <div className="space-y-2">
                {sicRates.map((r) => {
                  const selected = r.id === fareRateId;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setFareRateId(r.id)}
                      className={
                        'flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ' +
                        (selected
                          ? 'border-primary bg-primary/5 ring-primary/20 ring-1'
                          : 'border-zinc-200 hover:border-zinc-300')
                      }
                    >
                      <span
                        className={
                          'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ' +
                          (selected ? 'border-primary bg-primary text-white' : 'border-zinc-300')
                        }
                      >
                        {selected && <Check className="h-3 w-3" aria-hidden />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-start gap-2">
                          <span className="min-w-0 flex-1 text-sm font-medium break-words text-zinc-900">
                            {r.fareClassName ?? r.label}
                          </span>
                          {t.isMultiDayPass && r.validityDays != null && (
                            <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-500">
                              {r.validityDays} day
                            </span>
                          )}
                          {r.isBase && (
                            <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-500">
                              Base
                            </span>
                          )}
                        </div>
                        {showPrices && (
                          <p className="mt-0.5 text-xs text-zinc-500 tabular-nums">
                            {inr(r.perPersonPrice)} / person
                            {r.perPersonChildPrice && r.perPersonChildPrice > 0
                              ? ` · ${inr(r.perPersonChildPrice)} / child`
                              : ''}
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          </>
        ) : (
          // CAR: per-vehicle PRIVATE tiers and/or a single shared (SIC) option.
          <>
            <section>
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-xs font-semibold tracking-wider text-zinc-500 uppercase">
                  Service type
                </h4>
                <span className="inline-flex items-center gap-1 text-[11px] text-zinc-400">
                  <Users className="h-3 w-3" aria-hidden />
                  {adults} adult{adults === 1 ? '' : 's'}
                  {children > 0 ? ` · ${children} child${children === 1 ? '' : 'ren'}` : ''}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {hasPrivate && (
                  <TransferOption
                    label="Private"
                    price={showPrices ? (suggestion ? inr(suggestion.cost) : '-') : ''}
                    Icon={Car}
                    selected={mode === 'PRIVATE'}
                    onClick={() => setMode('PRIVATE')}
                  />
                )}
                {hasSic && (
                  <TransferOption
                    label="SIC"
                    price={showPrices ? inr((sicRate?.perPersonPrice ?? 0) * pax) : ''}
                    Icon={Bus}
                    selected={mode === 'SIC'}
                    onClick={() => setMode('SIC')}
                  />
                )}
              </div>
            </section>

            {mode === 'PRIVATE' && (
              <section className="space-y-3">
                <h4 className="text-xs font-semibold tracking-wider text-zinc-500 uppercase">
                  Vehicle
                </h4>
                <div className="space-y-2">
                  {privateRates.map((r) => {
                    const selected = r.id === rateId;
                    const cap = Math.max(1, r.paxMax);
                    const sugg = Math.max(1, Math.ceil(pax / cap));
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => {
                          setRateId(r.id);
                          setVehicles(sugg);
                        }}
                        className={
                          'flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ' +
                          (selected
                            ? 'border-primary bg-primary/5 ring-primary/20 ring-1'
                            : 'border-zinc-200 hover:border-zinc-300')
                        }
                      >
                        <span
                          className={
                            'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ' +
                            (selected ? 'border-primary bg-primary text-white' : 'border-zinc-300')
                          }
                        >
                          {selected && <Check className="h-3 w-3" aria-hidden />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-start gap-2">
                            <span className="min-w-0 flex-1 text-sm font-medium break-words text-zinc-900">
                              {r.vehicleTypeName ?? r.label}
                            </span>
                            {r.isBase && (
                              <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-500">
                                Base
                              </span>
                            )}
                          </div>
                          <p className="mt-0.5 text-xs text-zinc-500 tabular-nums">
                            {showPrices ? `${inr(r.pricePerVehicle)} / vehicle · ` : ''}up to{' '}
                            {r.paxMax} pax
                          </p>
                        </div>
                        <span className="shrink-0 text-[11px] text-zinc-400">suggests {sugg}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between rounded-lg border border-zinc-200 px-3 py-2">
                  <span className="text-sm text-zinc-600">Vehicles</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setVehicles((v) => Math.max(1, v - 1))}
                      className="inline-flex h-7 w-7 items-center justify-center rounded border border-zinc-200 text-lg leading-none text-zinc-600 hover:bg-zinc-50"
                      aria-label="Fewer vehicles"
                    >
                      -
                    </button>
                    <span className="w-8 text-center text-sm font-semibold tabular-nums">
                      {vehicles}
                    </span>
                    <button
                      type="button"
                      onClick={() => setVehicles((v) => v + 1)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded border border-zinc-200 text-lg leading-none text-zinc-600 hover:bg-zinc-50"
                      aria-label="More vehicles"
                    >
                      +
                    </button>
                  </div>
                </div>
              </section>
            )}

            {mode === 'SIC' && sicRate && (
              <p className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                <Bus className="h-3.5 w-3.5" aria-hidden />
                {showPrices
                  ? `Seat-in-coach · ${inr(sicRate.perPersonPrice)} per person × ${pax} pax.`
                  : `Seat-in-coach · ${pax} pax.`}
              </p>
            )}
          </>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-zinc-100 px-5 py-3.5">
        <div>
          {manualPrice ? (
            <div className="max-w-xs">
              <p className="text-[11px] tracking-wider text-zinc-400 uppercase">
                Long-haul destination
              </p>
              <p className="mt-0.5 text-xs text-zinc-500">
                Net price is auto-filled from the fare where set. Adjust it on the day row.
              </p>
            </div>
          ) : (
            <>
              <p className="text-[11px] text-zinc-400">
                {!isPublic && mode === 'PRIVATE'
                  ? `${vehicles} vehicle${vehicles === 1 ? '' : 's'}`
                  : `${pax} pax`}
              </p>
              {showPrices ? (
                <div className="flex items-baseline gap-2">
                  <p className="text-base font-semibold text-zinc-900 tabular-nums">{inr(cost)}</p>
                  <PriceDelta
                    base={currentCostPaise}
                    now={String(Math.max(0, Math.round(cost * 100)))}
                    currency="INR"
                  />
                </div>
              ) : (
                <PriceDelta
                  base={currentCostPaise}
                  now={String(Math.max(0, Math.round(cost * 100)))}
                  currency="INR"
                />
              )}
            </>
          )}
        </div>
        <UzButton onClick={add} loading={busy} disabled={!selectedRate}>
          <Plus className="mr-1.5 h-4 w-4" aria-hidden />
          Add to day
        </UzButton>
      </div>
    </div>
  );
};

const fmtInrPicker = (amount: number): string =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);

const ResultList = ({
  rows,
  addedIds,
  busy,
  showPrices,
  currentCostPaise,
  Icon,
  actionLabel,
  onAction,
  empty,
  header,
}: {
  rows: Array<{
    id: string;
    name: string;
    sub: string;
    meta?: string;
    imageUrl?: string | null;
    priceInr?: number | null;
    priceQualifier?: string | null;
    /** Per-row fallback avatar icon (e.g. the transfer's mode). Falls back to `Icon`. */
    RowIcon?: typeof Ticket;
    /** Small inline chip next to the name, e.g. "Multi-day pass". */
    badge?: string;
  }>;
  addedIds: Set<string>;
  busy: boolean;
  /** ADMIN-only: whether to render the sales price column (see modal root). */
  showPrices: boolean;
  /** When replacing, non-admins see +/- vs this baseline on each row. */
  currentCostPaise?: string | null;
  Icon: typeof Ticket;
  actionLabel: 'Add' | 'Choose';
  onAction: (id: string, name: string) => void;
  empty: string;
  /** 2026-07-07. Small count summary above the list (e.g. "12 of 45 activities").
   *  Rendered above the list body so the operator sees how much of the master
   *  catalogue matches their filters. Null hides it (before first search). */
  header?: string | null;
}): React.JSX.Element => {
  if (rows.length === 0) {
    return (
      <div className="space-y-2">
        {header && <p className="text-xs text-zinc-500">{header}</p>}
        <p className="py-10 text-center text-sm text-zinc-400">{empty}</p>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      {header && <p className="mb-2 text-xs text-zinc-500">{header}</p>}
      {rows.map((r) => {
        const added = addedIds.has(r.id);
        const RowIcon = r.RowIcon ?? Icon;
        return (
          <div
            key={r.id}
            className="flex items-center gap-3 rounded-lg border border-zinc-200 p-3 transition-colors hover:border-zinc-300"
          >
            {r.imageUrl ? (
              <div className="relative h-16 w-20 shrink-0 overflow-hidden rounded-md bg-zinc-50 ring-1 ring-zinc-200/60">
                <img
                  src={r.imageUrl}
                  alt={r.name}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            ) : (
              <span className="inline-flex h-16 w-20 shrink-0 items-center justify-center rounded-md bg-zinc-50 ring-1 ring-zinc-200/60">
                <RowIcon className="h-5 w-5 text-zinc-400" aria-hidden />
              </span>
            )}
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium break-words text-zinc-900">
                <span className="break-words">{r.name}</span>
                {r.badge && (
                  <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-500">
                    {r.badge}
                  </span>
                )}
              </p>
              <p className="mt-0.5 text-xs break-words text-zinc-500">
                {[r.sub, r.meta].filter(Boolean).join(' · ')}
              </p>
            </div>
            {showPrices && r.priceInr !== null && r.priceInr !== undefined && r.priceInr > 0 && (
              <div className="flex shrink-0 flex-col items-end text-right">
                <span className="text-[9px] font-medium tracking-wider text-zinc-400 uppercase">
                  From
                </span>
                <span className="text-sm font-semibold text-zinc-900 tabular-nums">
                  {fmtInrPicker(r.priceInr)}
                </span>
                {r.priceQualifier && (
                  <span className="text-[9px] text-zinc-400">{r.priceQualifier}</span>
                )}
              </div>
            )}
            {!showPrices &&
              currentCostPaise &&
              r.priceInr !== null &&
              r.priceInr !== undefined &&
              r.priceInr > 0 && (
                <div className="flex shrink-0 flex-col items-end">
                  <PriceDelta
                    base={currentCostPaise}
                    now={String(Math.round(r.priceInr * 100))}
                    currency="INR"
                  />
                </div>
              )}
            <UzButton
              variant={added ? 'outline' : 'default'}
              size="sm"
              className="h-7 shrink-0 text-xs"
              disabled={busy}
              onClick={() => onAction(r.id, r.name)}
            >
              {actionLabel === 'Choose' ? (
                <>
                  {added ? 'Added' : 'Choose'}
                  <ChevronRight className="ml-0.5 h-3 w-3" aria-hidden />
                </>
              ) : added ? (
                'Add again'
              ) : (
                <>
                  <Plus className="mr-1 h-3 w-3" aria-hidden /> Add
                </>
              )}
            </UzButton>
          </div>
        );
      })}
    </div>
  );
};
